import {
    Box,
    Container,
    Image,
    Spinner,
    Stack,
    Text,
    Badge,
    Grid,
} from "@chakra-ui/react";
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import SectionHeading from "../components/SectionHeading";
import { GET } from "../utilities/ApiProvider";
import { baseUrl } from "../utilities/customUrls";

export default function VideoGallery() {
    const [albums, setAlbums] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const navigate = useNavigate();

    const getAlbums = async () => {
        setIsLoading(true);
        setError(null);
        try {
            const response = await GET(`${baseUrl}/VideoAlbum/GetAlbums`, {
                authorization: `Basic ${btoa("admin:admin123")}`,
            });

            if (response?.Data && Array.isArray(response.Data)) {
                setAlbums(response.Data);
            } else {
                setAlbums([]);
                setError("No albums data received");
            }
        } catch (err) {
            console.error("Error fetching albums:", err);
            setError("Failed to load video albums. Please try again later.");
            setAlbums([]);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        getAlbums();
    }, []);

    const handleAlbumClick = (id, name) => {
        navigate(`/media/video-gallery/${id}`, {
            state: { albumName: name },
        });
    };

    return (
        <Stack py="80px">
            <Container maxW="6xl">
                <Stack pb={12}>
                    <SectionHeading
                        width="100%"
                        smallHeading="Videos"
                        mainHeading=""
                        marginPosition="auto"
                        textAlignment="center"
                    />
                </Stack>

                {error && (
                    <Text color="red.500" textAlign="center" mb={4}>
                        {error}
                    </Text>
                )}

                {isLoading ? (
                    <Stack align="center" justify="center" minH="200px">
                        <Spinner size="xl" />
                    </Stack>
                ) : albums.length === 0 ? (
                    <Stack align="center" justify="center" minH="200px">
                        <Text fontSize="lg" color="gray.500">
                            No video albums present
                        </Text>
                    </Stack>
                ) : (
                    <Grid
                        templateColumns="repeat(auto-fit, minmax(220px, 1fr))"
                        gap={6}
                        justifyItems="center"
                    >
                        {albums.map((album) => (
                            <Box
                                key={album.Id}
                                w="100%"
                                maxW="260px"
                                cursor="pointer"
                                transition="all 0.25s ease"
                                _hover={{ transform: "scale(1.05)", boxShadow: "xl" }}
                                onClick={() => handleAlbumClick(album.Id, album.AlbumName)}
                            >
                                <Box
                                    position="relative"
                                    borderRadius="md"
                                    overflow="hidden"
                                    boxShadow="md"
                                >
                                    <Image
                                        src={album.Thumbnail || "https://via.placeholder.com/260x200?text=Video+Album"}
                                        alt={album.AlbumName}
                                        w="100%"
                                        h="200px"
                                        objectFit="cover"
                                        loading="lazy"
                                    />

                                    {album.ActiveVideoCount > 0 && (
                                        <Badge
                                            position="absolute"
                                            bottom={2}
                                            right={2}
                                            px={3}
                                            py={1}
                                            borderRadius="full"
                                            fontSize="0.75rem"
                                            fontWeight="600"
                                            color="white"
                                            bg="rgba(0, 0, 0, 0.65)"
                                            backdropFilter="blur(4px)"
                                            boxShadow="sm"
                                        >
                                            {album.ActiveVideoCount}{" "}
                                            {album.ActiveVideoCount === 1 ? "Video" : "Videos"}
                                        </Badge>
                                    )}
                                </Box>

                                <Text mt={2} fontWeight="semibold" fontSize="md" noOfLines={2}>
                                    {album.AlbumName}
                                </Text>

                                {album.AlbumDescription && (
                                    <Text fontSize="sm" color="gray.600" noOfLines={2}>
                                        {album.AlbumDescription}
                                    </Text>
                                )}
                            </Box>
                        ))}
                    </Grid>
                )}
            </Container>
        </Stack>
    );
}